import AttachFileIcon from '@mui/icons-material/AttachFile';
import { SxProps, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import HighlightedText from '~components/HighlightedText/HighlightedText';
import { useOrderSearchHighlightTerm } from '~components/OrderSearchHighlightContext/OrderSearchHighlightContext';
import { Order } from '~hooks/useOrders/models';
import { makeOrderTitle } from '~utils/orders';

import OrderCardAttachmentPreview from './OrderCardAttachmentPreview';

interface OrderCardTitleProps {
  enableAttachmentPreview?: boolean;
  order: Order;
  prependWithOrderId?: boolean;
  sx?: SxProps<Theme>;
}

export default function OrderCardTitle({
  enableAttachmentPreview,
  order,
  prependWithOrderId,
  sx,
}: OrderCardTitleProps) {
  const text = makeOrderTitle(order, prependWithOrderId);
  const orderSearchHighlightTerm = useOrderSearchHighlightTerm();
  const attachmentCount = order.orderSummary?.fileAttachmentsCount ?? 0;
  const theme = useTheme();

  if (text) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 0.5,
        }}
      >
        <Typography
          data-test-id={`order-card-title-${order.orderId}`}
          sx={[
            () => ({ fontSize: '16px', fontWeight: 600 }),
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        >
          <HighlightedText searchTerm={orderSearchHighlightTerm} text={text} />
        </Typography>

        {attachmentCount > 0 &&
          (enableAttachmentPreview ? (
            <OrderCardAttachmentPreview
              attachmentCount={attachmentCount}
              orderId={order.id}
              orderNumber={order.orderId}
            />
          ) : (
            <Typography
              component="span"
              variant="body2"
              sx={{
                alignItems: 'center',
                color: theme.brandV2.colors.treadGreen,
                display: 'flex',
              }}
            >
              <AttachFileIcon fontSize="small" />
              {attachmentCount}
            </Typography>
          ))}
      </Box>
    );
  }

  return null;
}
